import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { HttpService } from 'src/app/services/http.service';
import { PortalRoleService } from 'src/app/services/portal-role.service';
import { environment } from 'src/environments/environment';

const STAGES = ['SUBSCRIPTION', 'INTAKE', 'REVIEW', 'BUILDOUT', 'APPROVAL', 'LAUNCH'];
const TASK_STATUSES = ['PENDING', 'ACTIVE', 'REVIEW', 'COMPLETE', 'CANCELED'];
const READINESS_LABELS: Record<string, string> = {
  hubDetails: 'Hub details',
  address: 'Address',
  gps: 'GPS',
  delivery: 'Delivery',
  seo: 'SEO',
  paymentMethod: 'Payment method',
  activities: 'Activities',
  orders: 'Orders',
  reviews: 'Received reviews',
};

interface TaskRow {
  id: string;
  description: string;
  status: string;
  dueDate: string | null;
  createdOn: string | null;
  editDescription: string;
  editStatus: string;
  editDueDate: string | null;
  saving: boolean;
}

@Component({
  selector: 'app-subscription-detail',
  templateUrl: './subscription-detail.component.html',
  styleUrls: ['./subscription-detail.component.scss'],
})
export class SubscriptionDetailComponent implements OnInit {
  private readonly opsRoyalBase = `${environment.apiUrl}/admin-center/ops-royal/v1`;
  readonly appUrl = environment.appUrl;
  readonly stages = STAGES;
  readonly stageOptions = STAGES.map((value) => ({ label: value, value }));
  readonly taskStatusOptions = TASK_STATUSES.map((value) => ({ label: value, value }));

  subscriptionId = '';
  detail: any = null;
  loading = true;
  saving = false;
  opsOptions: { label: string; value: string }[] = [];
  selectedOpsEmail: string | null = null;
  selectedStage: string | null = null;
  hubDefinitionDocUrl = '';
  customerVisibleNotes = '';

  tasks: TaskRow[] = [];
  tasksLoading = false;
  newTaskDescription = '';
  newTaskStatus = 'PENDING';
  newTaskDueDate: string | null = null;
  addingTask = false;

  isCreateHubDialogOpen = false;
  isCreatingHub = false;
  createHubName = '';
  createUrlHandler = '';
  createOpsEmail: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpService,
    private messages: MessageService,
    private confirm: ConfirmationService,
    public portalRole: PortalRoleService,
  ) {}

  get isAdmin(): boolean {
    return this.portalRole.isAdmin();
  }

  ngOnInit(): void {
    this.subscriptionId = this.route.snapshot.paramMap.get('subscriptionId') || '';
    this.loadOpsOptions();
    this.loadDetail();
  }

  readinessLabel(id: string): string {
    return READINESS_LABELS[id] || id;
  }

  back(): void {
    this.router.navigate(['/base/subscriptions']);
  }

  loadDetail(): void {
    this.loading = true;
    this.http
      .doGet(`${this.opsRoyalBase}/subscriptions/${encodeURIComponent(this.subscriptionId)}`)
      .subscribe({
        next: (d) => {
          this.detail = d;
          this.selectedOpsEmail = d.projectAssignedOpsEmail || null;
          const stageRaw = d.stage || d.projectStage || null;
          this.selectedStage = stageRaw ? String(stageRaw).toUpperCase() : null;
          this.hubDefinitionDocUrl = d.hubDefinitionDocUrl || '';
          this.customerVisibleNotes = d.customerVisibleNotes || '';
          this.tasks = this.mapTasks(d.tasks || []);
          this.loading = false;
          if (d.projectId) {
            this.loadTasks();
          }
        },
        error: (err) => {
          this.loading = false;
          this.messages.add({
            severity: 'error',
            summary: 'Error',
            detail: err?.error?.details || err?.message || 'Failed to load',
          });
        },
      });
  }

  loadTasks(): void {
    if (!this.detail?.projectId) {
      this.tasks = [];
      return;
    }
    this.tasksLoading = true;
    this.http
      .doGet(`${this.opsRoyalBase}/royal-projects/${this.detail.projectId}/tasks`)
      .subscribe({
        next: (rows) => {
          this.tasks = this.mapTasks(Array.isArray(rows) ? rows : rows?.tasks || []);
          this.tasksLoading = false;
        },
        error: (err) => {
          this.tasksLoading = false;
          this.messages.add({
            severity: 'error',
            summary: 'Error',
            detail: err?.error?.details || 'Failed to load tasks',
          });
        },
      });
  }

  private mapTasks(rows: any[]): TaskRow[] {
    return (rows || []).map((t) => {
      const dueDate = this.toDateOnly(t.dueDate);
      const status = (t.status || 'PENDING').toString().toUpperCase();
      return {
        id: String(t.id || t.taskId || ''),
        description: t.description || '',
        status,
        dueDate,
        createdOn: t.createdOn || t.createdAt || null,
        editDescription: t.description || '',
        editStatus: status,
        editDueDate: dueDate,
        saving: false,
      };
    });
  }

  private toDateOnly(value: any): string | null {
    if (!value) {
      return null;
    }
    const raw = String(value);
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
      return raw.slice(0, 10);
    }
    return raw;
  }

  addTask(): void {
    if (!this.detail?.projectId || !this.newTaskDescription.trim()) {
      return;
    }
    this.addingTask = true;
    const body: any = {
      description: this.newTaskDescription.trim(),
      status: this.newTaskStatus || 'PENDING',
    };
    const due = this.toDateOnly(this.newTaskDueDate);
    if (due) {
      body.dueDate = due;
    }
    this.http.doPost(`${this.opsRoyalBase}/royal-projects/${this.detail.projectId}/tasks`, body).subscribe({
      next: () => {
        this.addingTask = false;
        this.newTaskDescription = '';
        this.newTaskStatus = 'PENDING';
        this.newTaskDueDate = null;
        this.messages.add({ severity: 'success', summary: 'Task added' });
        this.loadTasks();
      },
      error: (err) => {
        this.addingTask = false;
        this.messages.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.details || 'Add task failed',
        });
      },
    });
  }

  saveTask(task: TaskRow): void {
    if (!this.detail?.projectId || !task.id || !task.editDescription.trim()) {
      return;
    }
    task.saving = true;
    const due = this.toDateOnly(task.editDueDate);
    const body: any = {
      description: task.editDescription.trim(),
      status: task.editStatus || 'PENDING',
    };
    if (due) {
      body.dueDate = due;
    } else if (task.dueDate) {
      body.clearDueDate = true;
    }
    this.http
      .doPatch(`${this.opsRoyalBase}/royal-projects/${this.detail.projectId}/tasks/${task.id}`, body)
      .subscribe({
        next: () => {
          task.saving = false;
          this.messages.add({ severity: 'success', summary: 'Task saved' });
          this.loadTasks();
        },
        error: (err) => {
          task.saving = false;
          this.messages.add({
            severity: 'error',
            summary: 'Error',
            detail: err?.error?.details || 'Save task failed',
          });
        },
      });
  }

  private loadOpsOptions(): void {
    this.http.doGet(`${this.opsRoyalBase}/portal-operators?role=OPS`).subscribe({
      next: (rows) => {
        this.opsOptions = (rows || [])
          .filter((r: any) => r.active)
          .map((r: any) => ({ label: r.email, value: r.email }));
      },
      error: () => {
        this.opsOptions = [];
      },
    });
  }

  assignProjectOps(): void {
    if (!this.isAdmin || !this.detail?.projectId) {
      return;
    }
    if (!this.selectedOpsEmail) {
      this.confirm.confirm({
        message: 'Unassign Ops from this Royal project?',
        header: 'Unassign Ops',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.http
            .doDelete(`${this.opsRoyalBase}/royal-projects/${this.detail.projectId}/assigned-ops`)
            .subscribe({
              next: () => {
                this.messages.add({ severity: 'success', summary: 'Unassigned' });
                this.loadDetail();
              },
              error: (err) =>
                this.messages.add({
                  severity: 'error',
                  summary: 'Error',
                  detail: err?.error?.details || 'Unassign failed',
                }),
            });
        },
      });
      return;
    }
    this.http
      .doPost(`${this.opsRoyalBase}/royal-projects/${this.detail.projectId}/assigned-ops`, {
        opsEmail: this.selectedOpsEmail,
      })
      .subscribe({
        next: () => {
          this.messages.add({ severity: 'success', summary: 'Assigned' });
          this.loadDetail();
        },
        error: (err) =>
          this.messages.add({
            severity: 'error',
            summary: 'Error',
            detail: err?.error?.details || 'Assign failed',
          }),
      });
  }

  saveProjectFields(patch: any): void {
    if (!this.detail?.projectId) {
      this.messages.add({
        severity: 'warn',
        summary: 'No project',
        detail: 'This customer has no Royal project yet.',
      });
      return;
    }
    this.saving = true;
    this.http.doPatch(`${this.opsRoyalBase}/royal-projects/${this.detail.projectId}`, patch).subscribe({
      next: () => {
        this.saving = false;
        this.messages.add({ severity: 'success', summary: 'Saved' });
        this.loadDetail();
      },
      error: (err) => {
        this.saving = false;
        this.messages.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.details || 'Save failed',
        });
      },
    });
  }

  saveStage(): void {
    if (!this.selectedStage) {
      return;
    }
    this.saveProjectFields({ stage: this.selectedStage });
  }

  saveDocUrl(): void {
    this.saveProjectFields({ hubDefinitionDocUrl: this.hubDefinitionDocUrl });
  }

  saveNotes(): void {
    this.saveProjectFields({ customerVisibleNotes: this.customerVisibleNotes });
  }

  hubUrl(): string | null {
    if (!this.detail?.hubId || !this.detail?.hubUrlHandler) {
      return null;
    }
    return `${this.appUrl}/${this.detail.hubUrlHandler}/client-view?hubId=${this.detail.hubId}`;
  }

  stripeUrl(): string | null {
    if (!this.detail?.canViewBilling || !this.detail?.stripeSubscriptionId) {
      return null;
    }
    return `https://dashboard.stripe.com/test/subscriptions/${this.detail.stripeSubscriptionId}`;
  }

  openCreateHub(): void {
    this.createHubName = this.detail?.userName || this.detail?.userEmail || 'Royal Hub';
    const seed = (this.detail?.userEmail || this.detail?.userName || 'royal')
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '')
      .slice(0, 20);
    this.createUrlHandler = seed || 'royalhub';
    this.createOpsEmail = this.detail?.projectAssignedOpsEmail || null;
    this.isCreateHubDialogOpen = true;
  }

  submitCreateHub(): void {
    if (!this.detail?.userId || !this.createHubName.trim() || !this.createUrlHandler.trim()) {
      return;
    }
    this.isCreatingHub = true;
    this.http
      .doPost(`${this.opsRoyalBase}/customers/${this.detail.userId}/hubs`, {
        name: this.createHubName.trim(),
        urlHandler: this.createUrlHandler.trim().toLowerCase(),
        opsEmail: this.createOpsEmail || undefined,
        assignCallerAsOps: !this.createOpsEmail && !this.isAdmin,
      })
      .subscribe({
        next: () => {
          this.isCreatingHub = false;
          this.isCreateHubDialogOpen = false;
          this.messages.add({ severity: 'success', summary: 'Hub created' });
          this.loadDetail();
        },
        error: (err) => {
          this.isCreatingHub = false;
          this.messages.add({
            severity: 'error',
            summary: 'Error',
            detail: err?.error?.details || 'Create hub failed',
          });
        },
      });
  }

  launchEditor(): void {
    if (!this.detail?.hubId) {
      return;
    }
    this.http.doPost(`${this.opsRoyalBase}/hubs/${this.detail.hubId}/editor-session`, {}).subscribe({
      next: (session) => {
        if (session?.launchUrl) {
          window.open(session.launchUrl, '_blank');
        }
      },
      error: (err) =>
        this.messages.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.details || 'Editor launch failed',
        }),
    });
  }
}